import { useMemo } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import { AsyncResult, PaginatedDataResult, useMappedStore, usePaginatedStore, useStoredData } from "@utils/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Group } from "@domain/group/group";
import { Media } from "@domain/media/media";
import { DetailedPerson } from "@domain/user/user";
import { Accreditation } from "./accreditation";
import { Organization } from "./organization";
import { Track } from "@domain/organization/track/track";

export function useMyOrganizations(): PaginatedDataResult<DetailedPerson> {
	const { organizationService } = useServices();
	return usePaginatedStore(organizationService.userOrganizations, []);
}

export function useOrganization(id: string, slug?: string): AsyncResult<Organization> {
	const { organizationService } = useServices();
	const currentProfile = useCurrentProfile();
	return useStoredData(
		slug ?? id,
		slug ? organizationService.slugOrganizationStore : organizationService.organizationStore,
		[currentProfile?.id]
	);
}

export function useSuggestedOrganizations(count: number): PaginatedDataResult<Organization> {
	const { organizationService } = useServices();
	const store = useMemo(
		() => new StorePresentation(organizationService.organizationStore, () => organizationService.suggest(count)),
		[count]
	);
	return usePaginatedStore(store, [count]);
}

export function useAccreditations(organizationId: string): PaginatedDataResult<Accreditation> {
	const { organizationService } = useServices();
	return useMappedStore(organizationId, organizationService.accreditationsStore, [organizationId]);
}

export function useOrganizationTracks(organizationId: string): PaginatedDataResult<Track> {
	const { organizationService } = useServices();
	return useMappedStore(organizationId, organizationService.tracksStore, [organizationId]);
}

export function useOrganizationAssets(organizationId: string): PaginatedDataResult<Media> {
	const { organizationService } = useServices();
	return useMappedStore(organizationId, organizationService.assetsStore, [organizationId]);
}

export function useOrganistationShareroom(organizationId: string): PaginatedDataResult<Group> {
	const { organizationService } = useServices();
	const currentProfile = useCurrentProfile();

	return useMappedStore(organizationId, organizationService.organizationShareroomsStore, [
		organizationId,
		currentProfile?.id,
	]);
}
